import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollToId } from "./Navbar";
import { useLang } from "../i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1474674556023-efef886fa147?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwZmFjdG9yeSUyMGludGVyaW9yJTIwaGVhdnklMjBtYWNoaW5lcnklMjBzdGVlbCUyMGNyYW5lfGVufDB8fHx8MTc4OTQ2OTU3NHww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const LINES = [t("hero_l1"), t("hero_l2"), t("hero_l3")];
  const METRICS = [
    { value: "1 663,8 m²", label: t("m1") },
    { value: "3,2 t", label: t("m2") },
    { value: t("m3v") !== "m3v" ? "až 3 MW" : "až 3 MW", label: t("m3") },
    { value: "3 057 m²", label: t("m4") },
  ];

  return (
    <section ref={ref} id="top" data-testid="hero-section"
      className="relative min-h-screen flex items-end overflow-hidden noise-overlay">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-24">
        <img src={HERO_IMG} alt="ZTS F-Invest" className="w-full h-full object-cover spotlight-img" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        <div className="absolute inset-0 blueprint-grid opacity-60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-8 pb-16 lg:pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          data-testid="hero-eyebrow"
          className="font-mono text-xs tracking-[0.3em] uppercase text-safety mb-6">
          {t("hero_eyebrow")}
        </motion.p>

        <h1 className="font-heading font-extrabold uppercase leading-[0.92] tracking-tight text-5xl sm:text-7xl lg:text-8xl text-slate-100">
          {LINES.map((line, i) => (
            <span key={`${line}-${i}`} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ delay: 0.3 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${i === 2 ? "text-safety" : ""}`}>
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
          {t("hero_sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          className="mt-8 flex flex-wrap gap-4">
          <button onClick={() => scrollToId("#kontakt")} data-testid="hero-cta-contact"
            className="group flex items-center gap-3 bg-safety text-ink font-heading font-bold uppercase tracking-wide text-lg px-7 py-3.5 hover:bg-safety-hover transition-colors duration-300">
            {t("hero_cta1")}
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button onClick={() => scrollToId("#priestory")} data-testid="hero-cta-spaces"
            className="group flex items-center gap-3 border border-slate-500 text-slate-200 font-heading font-bold uppercase tracking-wide text-lg px-7 py-3.5 hover:border-safety hover:text-safety transition-colors duration-300">
            {t("hero_cta2")}
            <ArrowDown className="w-5 h-5 transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          data-testid="hero-metrics"
          className="mt-14 grid grid-cols-2 lg:grid-cols-4 border-t border-steelborder/80 pt-6 gap-6">
          {METRICS.map((m) => (
            <div key={m.label}>
              <p className="font-heading text-3xl sm:text-4xl font-bold text-slate-100">{m.value}</p>
              <p className="font-mono text-xs tracking-[0.15em] uppercase text-slate-500 mt-1">{m.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
